import { ModelName } from '@casops/store/api';
import {
  AssetDeviceType,
  AssetComponentModel,
  RegisteredAssetDetailModel,
  useGetAssetQuery,
} from '@casops/store/apis/registeredAssetsApi';
import { ConnectivitySnackbar } from '@dmc/ui';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ColDef, RowClassParams, RowClassRules } from 'ag-grid-community';
import { useOutletContext } from 'react-router-dom';
import {
  columnDefAssetReportingDate,
  columnDefAssetReportingEvent,
  columnDefAssetReportingTime,
  columnDefAssetReportingWhen,
  columnDefBootloaderVersion,
  columnDefComponentId,
  columnDefComponentType,
  columnDefFirmwareVersion,
  columnDefHardwareRevision,
  columnDefSerialNumber,
  lockColumn,
} from '@casops/components/tables/grid/columnDefs';
import AssetDetailsMap from '@casops/components/maps/asset-details-map/AssetDetailsMap';
import {
  ReportingItem,
  useAssetReportingTableData,
} from '@casops/hooks/assets/useAssetReportingTableData';
import SingleAssetSubPage, {
  HeaderItem,
} from '@casops/templates/single-asset-sub-page/SingleAssetSubPage';
import InformationSection from '@casops/components/info-section/InformationSection';
import { AccordionStatus } from '@dmc/ui';
import { GridControlled } from '@casops/components/tables/grid/GridControlled';
import { useIsAssetManagement } from '@casops/permissions';

const GridSliceKeyReporting = 'AssetReporting';
const GridSliceKeyComponents = 'AssetComponents';

const getRowIdReporting = (data: ReportingItem) => data.evt;
const getRowIdComponents = (data: AssetComponentModel) => data.type;

const reportingRowClassRules: RowClassRules<ReportingItem> = {
  'dm-bold-row': (params: RowClassParams<ReportingItem>) => Boolean(params.data?.error),
};

export function AssetDetails() {
  const { t } = useTranslation();
  const isAssetManagement = useIsAssetManagement();
  const { data, requestRemainder } = useOutletContext<{
    data: RegisteredAssetDetailModel | undefined;
    requestRemainder: Omit<ReturnType<typeof useGetAssetQuery>, 'data'>;
  }>();

  const fetchError = requestRemainder.error;

  const headerItems = useMemo(() => {
    const items: HeaderItem[] = isAssetManagement
      ? [{ titleTransId: 'IpAddress', value: data?.ip }]
      : [];

    if (data?.hw !== AssetDeviceType.PPU) {
      items.unshift({ titleTransId: 'ModemSim', value: data?.mSim });
      items.unshift({ titleTransId: 'ModemImei', value: data?.mImei });
      items.unshift({ titleTransId: 'DeviceDescription', value: data?.dd });
    }

    return items;
  }, [data?.dd, data?.hw, data?.ip, data?.mImei, data?.mSim, isAssetManagement]);

  const { good: reportingGood, items: reportingItems } = useAssetReportingTableData({
    asset: data,
  });

  const columnDefsReporting: ColDef<ReportingItem>[] = useMemo(
    () => [
      { ...columnDefAssetReportingEvent(t) },
      { ...columnDefAssetReportingDate(t) },
      { ...columnDefAssetReportingTime(t) },
      { ...columnDefAssetReportingWhen(t) },
    ],
    [t],
  );

  const columnDefsComponents: ColDef<AssetComponentModel>[] = useMemo(
    () => [
      { ...columnDefComponentType(t, data?.hw) },
      { ...columnDefFirmwareVersion(t) },
      { ...columnDefBootloaderVersion(t) },
      { ...columnDefSerialNumber(t) },
      { ...columnDefHardwareRevision(t) },
      { ...columnDefComponentId(t) },
    ],
    [data?.hw, t],
  );

  return (
    <>
      <SingleAssetSubPage headerItems={headerItems} description={data?.desc}>
        <InformationSection
          status={reportingGood ? AccordionStatus.Healthy : AccordionStatus.Danger}
          icon={reportingGood ? 'good' : 'information'}
          title={t('ReportingStatus')}
        >
          <GridControlled<ReportingItem>
            sliceKey={GridSliceKeyReporting}
            rowData={reportingItems}
            columnDefs={columnDefsReporting}
            defaultColDef={lockColumn}
            getRowId={getRowIdReporting}
            rowClassRules={reportingRowClassRules}
            autoheight
          />
        </InformationSection>

        {data?.hw !== AssetDeviceType.PPU && (
          <InformationSection
            status={AccordionStatus.Informative}
            icon="asset"
            title={t('Components')}
          >
            <GridControlled<AssetComponentModel>
              sliceKey={GridSliceKeyComponents}
              rowData={data?.components}
              columnDefs={columnDefsComponents}
              getRowId={getRowIdComponents}
              noRowsMessage={t('No components available')}
              autoheight
            />
          </InformationSection>
        )}

        {data && data.posG && (
          <InformationSection
            status={AccordionStatus.Informative}
            icon="map"
            title={t('AssetLocation')}
            isPadded={false}
          >
            <AssetDetailsMap data={data} isFetching={requestRemainder.isFetching} />
          </InformationSection>
        )}
      </SingleAssetSubPage>
      <ConnectivitySnackbar
        error={fetchError}
        message={t('Model.Fetch.Error', { modelName: ModelName.AssetDetail })}
        title={t('Error')}
      />
    </>
  );
}

export default AssetDetails;
